'use client';

import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import cn from '@utils/cn';
import { rememberSelectedTrialPlan, TRIAL_PLAN_CODES } from '@components/Modules/PlanCards/utils/trialPlanCodes';
import { MouseEvent, ReactNode, useEffect, useMemo, useState } from 'react';

type BillingFrequency = 'monthly' | 'yearly';

type PlanPrice = {
  price: string;
  priceUnit: string;
  priceNote: string;
};

type PopinFeature = { label: string; value: string };

const TRIAL_SIGNUP_URL =
  'https://app.thebrief.ai/auth/create-account?internal_source=internal&internal_medium=PricingPage&internal_campaign=StartTrial';

const ENTERPRISE_CONTACT_URL =
  'https://www.thebrief.ai/get-in-touch-enterprise/?internal_source=internal&internal_medium=PricingPage&internal_campaign=PricingPage';

type Plan = {
  category: string;
  name: string;
  description: string;
  prices: Record<BillingFrequency, PlanPrice>;
  ctaLabel: string;
  ctaHref: string;
  trialPlanCode?: string;
  ctaVariant: 'primary' | 'outline';
  ctaNote: string;
  featuresIntro?: string;
  features: PopinFeature[];
  limitNote: string;
  theme: 'light' | 'dark';
  badge?: string;
  popinCtaLabel: string;
};

const proPlan: Plan = {
  category: 'Create',
  name: 'Pro',
  description: 'Design every kind of ad on a single seat.',
  prices: {
    yearly: { price: '29', priceUnit: '/ month', priceNote: 'Billed annually at $348' },
    monthly: { price: '39', priceUnit: '/ month', priceNote: 'Billed monthly' },
  },
  ctaLabel: 'Start free trial',
  ctaHref: TRIAL_SIGNUP_URL,
  trialPlanCode: TRIAL_PLAN_CODES.pro,
  ctaVariant: 'outline',
  ctaNote: 'No card required · 7-day trial',
  features: [
    { label: 'Design', value: 'Ad Studio + Canvas, 5,000+ templates' },
    { label: 'AI', value: 'image, video, copy, resize — included for normal use' },
    { label: 'Export', value: 'JPG, PNG, WEBP, GIF, HTML5, MP4, PDF, AMP' },
    { label: 'Workspace', value: '5 Brand Kits, 250 GB storage, 1 workspace' },
    { label: 'Integrations', value: 'Shopify, Google Drive, Dropbox' },
  ],
  limitNote: "We'll reach out before any limit — never a surprise stop.",
  theme: 'light',
  popinCtaLabel: 'Choose plan',
};

const ultraPlan: Plan = {
  category: 'Amplify',
  name: 'Ultra',
  description: 'High-volume creation, ad serving and publishing.',
  prices: {
    yearly: { price: '79', priceUnit: '/ month', priceNote: 'Billed annually at $948' },
    monthly: { price: '99', priceUnit: '/ month', priceNote: 'Billed monthly' },
  },
  ctaLabel: 'Start free trial',
  ctaHref: TRIAL_SIGNUP_URL,
  trialPlanCode: TRIAL_PLAN_CODES.ultra,
  ctaVariant: 'primary',
  ctaNote: 'No card required · 7-day trial',
  featuresIntro: 'Everything in Pro, plus:',
  features: [
    { label: 'Capacity', value: 'expanded AI & export for sustained heavy use' },
    { label: 'Ad serving', value: '25+ networks + publish to Meta & Google Ads' },
    { label: 'Localization', value: 'AI Translate for multi-market campaigns' },
    { label: 'Optimization', value: 'automated A/B testing + shareable performance reports' },
    { label: 'Workspace', value: '20 Brand Kits, 1 TB storage' },
  ],
  limitNote: "We'll reach out before any limit — never a surprise stop.",
  theme: 'dark',
  badge: 'Best value',
  popinCtaLabel: 'Choose plan',
};

const teamPlan: Plan = {
  category: 'Collaborate',
  name: 'Team',
  description: 'Build ads together with shared brand systems.',
  prices: {
    yearly: { price: '49', priceUnit: '/seat / mo', priceNote: 'Billed annually at $588 / seat' },
    monthly: { price: '59', priceUnit: '/seat / mo', priceNote: 'Billed monthly · save 17% with annual' },
  },
  ctaLabel: 'Start free trial',
  ctaHref: TRIAL_SIGNUP_URL,
  trialPlanCode: TRIAL_PLAN_CODES.team,
  ctaVariant: 'outline',
  ctaNote: 'No card required · 7-day trial',
  featuresIntro: 'Everything in Pro, plus:',
  features: [
    { label: 'Seats', value: '2–10 with roles & permissions' },
    { label: 'Capacity', value: 'AI & export scales with seats' },
    { label: 'Collaboration', value: 'real-time co-editing in Canvas, version history' },
    { label: 'Brand systems', value: 'shared templates, team font library, locked elements' },
    { label: 'Ad serving', value: '25+ networks + publish to Meta & Google Ads' },
  ],
  limitNote: 'Need 11+ seats, SSO or DAM? See Enterprise.',
  theme: 'light',
  popinCtaLabel: 'Choose plan',
};

const enterprisePrice: PlanPrice = {
  price: "Let's talk",
  priceUnit: '',
  priceNote: 'Monthly, quarterly or annual · usage-based',
};

const enterprisePlan: Plan = {
  category: 'Scale',
  name: 'Enterprise',
  description: 'Mass production, bulk distribution, governance.',
  prices: { yearly: enterprisePrice, monthly: enterprisePrice },
  ctaLabel: 'Talk to sales',
  ctaHref: ENTERPRISE_CONTACT_URL,
  ctaVariant: 'outline',
  ctaNote: 'Custom contract · dedicated support',
  featuresIntro: 'Everything in Team, plus:',
  features: [
    { label: 'Mass production', value: 'feeds, real-time data, A/B testing, sticky ads' },
    { label: 'Bulk distribution', value: 'API, white-label editor, CM360 / DV360 / Veeva' },
    { label: 'Brand assets', value: 'DAM (Bynder, Frontify, Orange Logic)' },
    { label: 'Governance', value: 'SSO/SAML/SCIM, custom roles, user groups, ISO audits' },
    { label: 'Reporting', value: 'detailed usage tracking + automated reports (Email & Slack)' },
    { label: 'Support & terms', value: 'dedicated AM, priority SLAs, onboarding, contractual capacity' },
  ],
  limitNote: "We'll reach out before any limit — never a surprise stop.",
  theme: 'light',
  popinCtaLabel: 'Talk to sales',
};

const tierColumns: Plan[] = [proPlan, ultraPlan, teamPlan, enterprisePlan];

const toolkitItems = [
  {
    category: 'Design',
    description: 'Full Ad Studio + Canvas, Magic Animator, 5,000+ templates, Photoshop & Figma import.',
  },
  {
    category: 'AI',
    description: 'Image, video, copy, resize and background generation. Included with every plan, no credits.',
  },
  {
    category: 'Export',
    description: 'JPG, PNG, MP4, GIF, HTML5 and PDF. All formats included, unmetered.',
  },
];

type TeamAnswer = 'solo' | 'small' | 'large';
type VolumeAnswer = 'low' | 'mid' | 'high';
type GovernanceAnswer = 'no' | 'yes';
type RecommenderQuestionKey = 'team' | 'volume' | 'governance';

type RecommenderAnswers = {
  team: TeamAnswer | null;
  volume: VolumeAnswer | null;
  governance: GovernanceAnswer | null;
};

type RecommendedPlanKey = 'pro' | 'ultra' | 'team' | 'enterprise';

type RecommendationReason =
  | 'pro_solo_low'
  | 'ultra_solo_volume'
  | 'team_small_collab'
  | 'enterprise_small_team_high_volume'
  | 'enterprise_large_team'
  | 'enterprise_governance';

type Recommendation = {
  plan: RecommendedPlanKey;
  reason: RecommendationReason;
};

type RecommenderQuestion =
  | { key: 'team'; label: string; options: { id: TeamAnswer; label: string }[] }
  | { key: 'volume'; label: string; options: { id: VolumeAnswer; label: string }[] }
  | { key: 'governance'; label: string; options: { id: GovernanceAnswer; label: string }[] };

const RECOMMENDER_QUESTIONS: RecommenderQuestion[] = [
  {
    key: 'team',
    label: "Who's using it?",
    options: [
      { id: 'solo', label: 'Just me' },
      { id: 'small', label: '2–10 people' },
      { id: 'large', label: '10+ people / multiple departments' },
    ],
  },
  {
    key: 'volume',
    label: 'How many ads do you ship a month?',
    options: [
      { id: 'low', label: 'Under 100' },
      { id: 'mid', label: '100-500' },
      { id: 'high', label: '500+' },
    ],
  },
  {
    key: 'governance',
    label: 'Do you need governance — SSO, brand control, white-label?',
    options: [
      { id: 'no', label: 'Not really' },
      { id: 'yes', label: "Yes — it's a requirement" },
    ],
  },
];

const RECOMMENDER_TOTAL_STEPS = RECOMMENDER_QUESTIONS.length;

const RECOMMENDATION_REASON_TEXT: Record<RecommendationReason, string> = {
  pro_solo_low:
    'Pro covers a solo workflow with low ad volume — unlimited designs, brand kits, and core creative tools.',
  ultra_solo_volume:
    'Ultra is built for solo creators with higher ad volume — high-capacity AI, expandable credits, and impressions.',
  team_small_collab:
    'Team gives a 2–10 person team the collaboration it needs — seats, roles & permissions, and brand consistency.',
  enterprise_small_team_high_volume:
    'Enterprise handles high ad volume at team scale — dedicated support, contractual usage, and integrations.',
  enterprise_large_team:
    'Enterprise scales for 10+ people across departments — flexible seats, unlimited brand kits, and integrations.',
  enterprise_governance:
    'Enterprise covers your governance needs — SSO, brand control, white-label, and dedicated support.',
};

const RECOMMENDED_PLAN_DISPLAY: Record<
  RecommendedPlanKey,
  { name: string; ctaLabel: string; ctaHref: string; trialPlanCode?: string }
> = {
  pro: { name: 'Pro', ctaLabel: 'Start free trial', ctaHref: TRIAL_SIGNUP_URL, trialPlanCode: TRIAL_PLAN_CODES.pro },
  ultra: {
    name: 'Ultra',
    ctaLabel: 'Start free trial',
    ctaHref: TRIAL_SIGNUP_URL,
    trialPlanCode: TRIAL_PLAN_CODES.ultra,
  },
  team: { name: 'Team', ctaLabel: 'Start free trial', ctaHref: TRIAL_SIGNUP_URL, trialPlanCode: TRIAL_PLAN_CODES.team },
  enterprise: { name: 'Enterprise', ctaLabel: 'Talk to sales', ctaHref: ENTERPRISE_CONTACT_URL },
};

function recommendPlan(answers: RecommenderAnswers): Recommendation | null {
  const { team, volume, governance } = answers;

  if (governance === 'yes') {
    return { plan: 'enterprise', reason: 'enterprise_governance' };
  }
  if (team === 'large') {
    return { plan: 'enterprise', reason: 'enterprise_large_team' };
  }
  if (team === null || volume === null || governance === null) {
    return null;
  }
  if (team === 'solo') {
    if (volume === 'low') {
      return { plan: 'pro', reason: 'pro_solo_low' };
    }
    return { plan: 'ultra', reason: 'ultra_solo_volume' };
  }
  if (volume === 'high') {
    return { plan: 'enterprise', reason: 'enterprise_small_team_high_volume' };
  }
  return { plan: 'team', reason: 'team_small_collab' };
}

const EMPTY_RECOMMENDER_ANSWERS: RecommenderAnswers = { team: null, volume: null, governance: null };

const BRAND_RED = '#ca5936';
const BRAND_RED_LIGHT = '#ff774d';
const BRAND_BEIGE = '#f0efed';

type PricingVariantBProps = {
  faqSlot?: ReactNode;
  comparerSlot?: ReactNode;
};

export default function PricingVariantB({ faqSlot, comparerSlot }: PricingVariantBProps) {
  const [billing, setBilling] = useState<BillingFrequency>('yearly');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);

  return (
    <main className="font-the-future bg-background flex min-w-[320px] flex-col">
      <HeroSection />
      <FeatureListBar onSelectPlan={setSelectedPlan} />
      <BillingToggle billing={billing} onChange={setBilling} />
      <PlanSection
        unicase="For Individuals"
        title="Single-seat plans for solo creators."
        description="Pro is for designers and freelancers doing normal professional work. Ultra is for sustained high-volume work, ad serving through The Brief, and multi-market campaigns."
        plans={[proPlan, ultraPlan]}
        billing={billing}
      />
      <PlanSection
        unicase="For Businesses"
        title="Plans for teams and organizations."
        description="Team is for 2–10 people in one workspace, with shared brand systems, roles, real-time co-editing, and pooled ad-serving capacity. Enterprise is for organizations that mass-produce ads through feeds and live data, distribute through the API or DAM, and need governance and contractual capacity."
        plans={[teamPlan, enterprisePlan]}
        billing={billing}
      />
      <AgencyCta />
      <ToolkitSection />
      <PlanRecommender />
      {/* <FeatureMatrixCta /> */}
      {comparerSlot}
      {faqSlot}
      {selectedPlan && <PlanPopin plan={selectedPlan} billing={billing} onClose={() => setSelectedPlan(null)} />}
    </main>
  );
}

function HeroSection() {
  return (
    <section className="mx-auto flex w-full max-w-7xl flex-col items-center gap-5 px-4 py-8 pt-25! text-center sm:px-8 sm:py-12">
      <h1 className="text-4xl font-medium leading-[1.2] text-[#121214] sm:text-5xl md:text-[48px]">
        The right plan for <span style={{ color: BRAND_RED }}>how you work.</span>
      </h1>
      <p className="max-w-2xl text-base leading-[1.5] sm:text-lg" style={{ color: '#51535b' }}>
        Every plan includes the full Ad Studio, AI generation and export. Pick by how much you ship and who you ship
        with.
      </p>
      <p
        className="text-foreground/60 text-[12px] font-medium uppercase tracking-[0.48px]"
        style={{ color: '#51535b' }}
      >
        no credits. no counters. just plans
      </p>
    </section>
  );
}

function FeatureListBar({ onSelectPlan }: { onSelectPlan: (plan: Plan) => void }) {
  return (
    <section className="bg-primary-no-alpha hidden w-full pt-0! md:block">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-2 gap-x-6 gap-y-4 px-4 py-6 sm:px-8 md:grid-cols-4 md:gap-x-8">
        {tierColumns.map((plan) => (
          <button
            key={plan.name}
            type="button"
            onClick={() => onSelectPlan(plan)}
            className="flex cursor-pointer flex-col items-center gap-1 rounded-md text-center transition-opacity hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
          >
            <p className="text-base font-medium" style={{ color: BRAND_RED_LIGHT }}>
              {plan.category}
            </p>
            <p className="text-sm text-white">{plan.name}</p>
          </button>
        ))}
      </div>
    </section>
  );
}

type BillingToggleProps = {
  billing: BillingFrequency;
  onChange: (next: BillingFrequency) => void;
};

function BillingToggle({ billing, onChange }: BillingToggleProps) {
  const handleScrollToRecommender = (event: MouseEvent<HTMLAnchorElement>) => {
    // Respect modifier clicks (cmd/ctrl/shift, middle-click) so users can still
    // open the anchor in a new tab. Only intercept the plain primary click.
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
      return;
    }
    const target = document.getElementById('plan-recommender');
    if (!target) {
      return;
    }
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="mx-auto flex w-full max-w-7xl flex-col items-center justify-center gap-4 px-4 py-6 sm:px-8 md:flex-row md:gap-6">
      <div
        className="inline-flex h-[42px] items-center rounded-full border p-1"
        style={{ borderColor: '#c7c8cb' }}
        role="tablist"
        aria-label="Billing frequency"
      >
        <button
          type="button"
          role="tab"
          aria-selected={billing === 'monthly'}
          onClick={() => onChange('monthly')}
          className={cn(
            'flex h-full items-center justify-center rounded-full px-6 text-sm font-medium transition-colors',
            billing === 'monthly' ? 'text-white' : 'text-foreground',
          )}
          style={billing === 'monthly' ? { backgroundColor: '#292b35' } : undefined}
        >
          Monthly
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={billing === 'yearly'}
          onClick={() => onChange('yearly')}
          className={cn(
            'flex h-full items-center justify-center gap-2 rounded-full px-4 text-sm font-medium transition-colors',
            billing === 'yearly' ? 'text-white' : 'text-foreground',
          )}
          style={billing === 'yearly' ? { backgroundColor: '#292b35' } : undefined}
        >
          Yearly
          <span
            className="rounded-full px-2 py-px text-[12px] font-medium tracking-[0.18px]"
            style={{ backgroundColor: '#ffcdbd', color: BRAND_RED }}
          >
            save -26%
          </span>
        </button>
      </div>
      <a
        href="#plan-recommender"
        onClick={handleScrollToRecommender}
        className="text-sm font-regular text-[#292b35] underline-animate-left-right hover:opacity-80"
      >
        Not sure where to start? →
      </a>
    </section>
  );
}

function ToolkitSection() {
  return (
    <section className="mx-auto hidden w-full max-w-7xl border-b border-black/10 px-4 py-8 sm:px-8 md:block">
      <div className="flex flex-col items-center gap-6">
        <p className="text-[12px] font-medium uppercase tracking-[0.48px]" style={{ color: '#51535b' }}>
          Same toolkit on every plan
        </p>
        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
          {toolkitItems.map((item) => (
            <div
              key={item.category}
              className="flex flex-col items-start gap-2 px-6 py-6"
              style={{ backgroundColor: BRAND_BEIGE }}
            >
              <p className="text-[12px] font-medium uppercase tracking-[0.48px]" style={{ color: BRAND_RED }}>
                {item.category}
              </p>
              <p className="text-sm leading-[1.6]" style={{ color: '#51535b' }}>
                {item.description}
              </p>
            </div>
          ))}
        </div>
        <p className="text-center text-sm" style={{ color: '#51535b' }}>
          Every plan ships with the same essentials. Capacity, seats and advanced features scale with the plan.
        </p>
      </div>
    </section>
  );
}

type PlanSectionProps = {
  unicase: string;
  title: string;
  description: string;
  plans: [Plan, Plan];
  billing: BillingFrequency;
};

function PlanSection({ unicase, title, description, plans, billing }: PlanSectionProps) {
  return (
    <section className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-8 md:py-20">
      <div className="mb-12 grid grid-cols-1 items-start gap-6 md:grid-cols-2 md:gap-8">
        <div className="flex flex-col gap-1">
          <p className="text-[12px] font-medium uppercase tracking-[0.48px]" style={{ color: '#51535b' }}>
            {unicase}
          </p>
          <h2 className="text-3xl font-medium leading-[1.2] text-foreground md:text-4xl">{title}</h2>
        </div>
        <p className="font-sans text-base leading-6 text-body-foreground">{description}</p>
      </div>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        {plans.map((plan) => (
          <PlanCard key={plan.name} plan={plan} billing={billing} />
        ))}
      </div>
    </section>
  );
}

function PlanCard({ plan, billing }: { plan: Plan; billing: BillingFrequency }) {
  const isDark = plan.theme === 'dark';
  const { price, priceUnit, priceNote } = plan.prices[billing];
  const isPriceText = !/^\d/.test(price);

  return (
    <div
      className={cn(
        'relative flex h-full flex-col gap-4 border p-8',
        isDark ? 'border-black bg-black text-white' : 'bg-white',
      )}
      style={isDark ? undefined : { borderColor: 'rgba(0,0,0,0.1)' }}
    >
      {plan.badge && (
        <span
          className="absolute -top-3 left-8 rounded-full px-3 py-1 text-[12px] font-medium uppercase tracking-[0.48px] text-white"
          style={{ backgroundColor: BRAND_RED }}
        >
          {plan.badge}
        </span>
      )}
      <p
        className="text-center text-[12px] font-medium uppercase tracking-[0.48px] md:text-left"
        style={{ color: BRAND_RED }}
      >
        {plan.category}
      </p>
      <p
        className={cn(
          'text-center text-[36px] font-medium leading-[1.3] md:text-left',
          isDark ? 'text-white' : 'text-black',
        )}
      >
        {plan.name}
      </p>
      <p
        className="text-center text-sm leading-[1.6] md:text-left"
        style={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#51535b' }}
      >
        {plan.description}
      </p>

      <div className="flex flex-col items-center gap-1.5 md:items-start">
        <div className="flex items-end justify-center gap-1.5 md:justify-start">
          {isPriceText ? (
            <p className={cn('text-[36px] font-medium leading-[1.3]', isDark ? 'text-white' : 'text-black')}>{price}</p>
          ) : (
            <>
              <span className={cn('pb-3 text-xl font-medium leading-none', isDark ? 'text-white' : 'text-black')}>
                $
              </span>
              <span
                className={cn(
                  'text-[56px] font-medium leading-[1] tracking-[-2px]',
                  isDark ? 'text-white' : 'text-black',
                )}
              >
                {price}
              </span>
              <span
                className="pb-3 text-[12px] font-medium leading-[1.5] tracking-[0.18px]"
                style={{ color: isDark ? 'rgba(255,255,255,0.6)' : '#51535b' }}
              >
                {priceUnit}
              </span>
            </>
          )}
        </div>
        <p className="text-[12px] leading-[1.5]" style={{ color: isDark ? 'rgba(255,255,255,0.6)' : '#51535b' }}>
          {priceNote}
        </p>
      </div>

      <PlanCtaButton plan={plan} />

      <p
        className="text-center text-[12px] font-medium leading-[1.5] tracking-[0.18px]"
        style={{ color: isDark ? 'rgba(255,255,255,0.6)' : '#51535b' }}
      >
        {plan.ctaNote}
      </p>

      <div className="h-px w-full" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)' }} />

      {plan.featuresIntro && (
        <p className="text-[13px] font-medium leading-[1.5]" style={{ color: BRAND_RED }}>
          {plan.featuresIntro}
        </p>
      )}

      <ul className="flex flex-col gap-2.5">
        {plan.features.map((feature) => (
          <li key={feature.label} className="flex items-start gap-2.5">
            <CheckIcon
              className="mt-1 size-3.5 shrink-0"
              strokeWidth={2.5}
              style={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'black' }}
            />
            <span className="text-base leading-[1.5]" style={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'black' }}>
              <span className="font-medium">{feature.label}:</span> {feature.value}
            </span>
          </li>
        ))}
      </ul>

      <p
        className="mt-auto pt-2 text-[12px] leading-[1.5]"
        style={{ color: isDark ? 'rgba(255,255,255,0.6)' : '#51535b' }}
      >
        {plan.limitNote}
      </p>
    </div>
  );
}

function PlanCtaButton({ plan }: { plan: Plan }) {
  const isPrimary = plan.ctaVariant === 'primary';

  return (
    <a
      href={plan.ctaHref}
      onClick={() => rememberSelectedTrialPlan(plan.trialPlanCode)}
      className={cn(
        'inline-flex h-12 w-full items-center justify-center rounded-full px-5 text-sm font-medium tracking-[0.1px] transition-opacity hover:opacity-80',
        isPrimary ? 'text-white' : 'border bg-white text-black',
      )}
      style={isPrimary ? { backgroundColor: BRAND_RED } : { borderColor: 'rgba(0,0,0,0.25)' }}
    >
      {plan.ctaLabel}
    </a>
  );
}

function AgencyCta() {
  return (
    <section className="bg-black px-4 py-10 sm:px-8">
      <div className="mx-auto flex w-full max-w-7xl flex-col items-start gap-6 md:flex-row md:items-center md:justify-between md:gap-12">
        <div className="flex max-w-2xl flex-col gap-2">
          <h2 className="text-3xl font-medium leading-[1.3] text-white md:text-[36px]">Agency Package</h2>
          <p className="text-sm leading-[1.65] tracking-[0.1px]" style={{ color: '#e6e8eb' }}>
            For agencies running campaigns for multiple brands. Layers Brand Kits per client, white-label exports,
            branded share links and predictable per-client billing on top of Team or Enterprise.
          </p>
        </div>
        <a
          href={ENTERPRISE_CONTACT_URL}
          className="inline-flex h-12 shrink-0 items-center justify-center rounded-full px-5 text-sm font-medium tracking-[0.1px] text-white transition-opacity hover:opacity-80"
          style={{ backgroundColor: BRAND_RED }}
        >
          Get a custom plan
        </a>
      </div>
    </section>
  );
}

function PlanRecommender() {
  const [answers, setAnswers] = useState<RecommenderAnswers>(EMPTY_RECOMMENDER_ANSWERS);
  const [step, setStep] = useState(0);

  const recommendation = useMemo(() => recommendPlan(answers), [answers]);

  const handleAnswer = (key: RecommenderQuestionKey, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    if (step + 1 < RECOMMENDER_TOTAL_STEPS) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step <= 0) {
      return;
    }
    const prevStep = step - 1;
    const prevKey = RECOMMENDER_QUESTIONS[prevStep].key;
    setAnswers((prev) => ({ ...prev, [prevKey]: null }));
    setStep(prevStep);
  };

  const handleStartOver = () => {
    setAnswers(EMPTY_RECOMMENDER_ANSWERS);
    setStep(0);
  };

  const completedSegments = recommendation ? RECOMMENDER_TOTAL_STEPS : step;

  return (
    <section id="plan-recommender" className="bg-background px-4 py-12 sm:px-8">
      <div
        className="mx-auto flex w-full max-w-2xl flex-col gap-6 p-8"
        style={{ backgroundColor: BRAND_BEIGE }}
        role="region"
        aria-labelledby="plan-recommender-heading"
      >
        <div className="flex flex-col gap-2">
          <p className="text-[12px] font-medium uppercase tracking-[0.48px]" style={{ color: '#51535b' }}>
            Plan Recommender
          </p>
          <h2 id="plan-recommender-heading" className="text-3xl font-medium leading-[1.2] text-foreground md:text-4xl">
            Find your fit in 3 questions
          </h2>
        </div>

        {!recommendation && (
          <div
            className="flex items-center gap-2"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={RECOMMENDER_TOTAL_STEPS}
            aria-valuenow={step + 1}
            aria-label={`Question ${step + 1} of ${RECOMMENDER_TOTAL_STEPS}`}
          >
            {Array.from({ length: RECOMMENDER_TOTAL_STEPS }).map((_, index) => (
              <span
                key={index}
                className="h-1.5 w-12 rounded-full"
                style={{ backgroundColor: index <= completedSegments ? BRAND_RED : '#c7c8cb' }}
              />
            ))}
          </div>
        )}

        {recommendation ? (
          <RecommenderResult recommendation={recommendation} onStartOver={handleStartOver} />
        ) : (
          <RecommenderQuestionView
            question={RECOMMENDER_QUESTIONS[step]}
            step={step}
            onAnswer={handleAnswer}
            onBack={handleBack}
          />
        )}
      </div>
    </section>
  );
}

type RecommenderQuestionViewProps = {
  question: RecommenderQuestion;
  step: number;
  onAnswer: (key: RecommenderQuestionKey, value: string) => void;
  onBack: () => void;
};

function RecommenderQuestionView({ question, step, onAnswer, onBack }: RecommenderQuestionViewProps) {
  return (
    <>
      <p className="font-sans text-base leading-[1.5]" style={{ color: '#51535b' }}>
        {question.label}
      </p>
      <div className="flex flex-col gap-2">
        {question.options.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => onAnswer(question.key, option.id)}
            className="inline-flex w-full cursor-pointer items-center rounded-full border bg-white px-5 py-3 text-left text-sm transition-colors hover:bg-black/5"
            style={{ borderColor: 'rgba(0,0,0,0.25)' }}
          >
            {option.label}
          </button>
        ))}
      </div>
      {step > 0 && (
        <button
          type="button"
          onClick={onBack}
          className="self-start text-sm font-medium text-[#292b35] underline-animate-left-right hover:opacity-80"
        >
          ← Back
        </button>
      )}
    </>
  );
}

function RecommenderResult({
  recommendation,
  onStartOver,
}: {
  recommendation: Recommendation;
  onStartOver: () => void;
}) {
  const plan = RECOMMENDED_PLAN_DISPLAY[recommendation.plan];

  return (
    <div
      className="flex flex-col items-center gap-3 text-center"
      data-testid={`recommender-result-${recommendation.plan}`}
    >
      <p className="text-[12px] font-medium uppercase tracking-[0.48px]" style={{ color: '#51535b' }}>
        We recommend
      </p>
      <p className="text-3xl font-medium leading-[1.2] text-foreground md:text-4xl">{plan.name}</p>
      <p className="font-sans text-base leading-[1.5]" style={{ color: '#51535b' }}>
        {RECOMMENDATION_REASON_TEXT[recommendation.reason]}
      </p>
      <a
        href={plan.ctaHref}
        onClick={() => rememberSelectedTrialPlan(plan.trialPlanCode)}
        className="mt-4 inline-flex h-12 w-full items-center justify-center rounded-full px-5 text-sm font-medium tracking-[0.1px] text-white transition-opacity hover:opacity-80"
        style={{ backgroundColor: BRAND_RED }}
      >
        {plan.ctaLabel}
      </a>
      <button
        type="button"
        onClick={onStartOver}
        className="text-sm font-medium text-[#292b35] underline-animate-left-right hover:opacity-80"
      >
        Start over
      </button>
    </div>
  );
}

//  function FeatureMatrixCta() {
//   return (
//     <section className="bg-background hidden px-4 py-8 sm:px-8 md:block">
//       <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 bg-black p-8 md:flex-row md:items-center md:gap-12 md:p-12">
//         <div className="flex max-w-xl flex-col gap-2">
//           <p className="text-[12px] font-medium uppercase tracking-[0.48px]" style={{ color: BRAND_RED }}>
//             Compare every feature
//           </p>
//           <h2 className="text-3xl font-medium leading-[1.3] text-white md:text-[36px]">The full feature matrix.</h2>
//           <p className="text-base leading-[1.5]" style={{ color: '#e6e8eb' }}>
//             200+ features across Pro, Ultra, Team and Enterprise. Search, filter to differences only, see at a glance
//             what&apos;s where.
//           </p>
//         </div>
//         <div className="flex flex-1 flex-col gap-6 md:flex-row md:items-center md:justify-end md:gap-10">
//           <div className="grid grid-cols-3 gap-6 md:flex md:flex-col md:gap-4">
//             {matrixStats.map((stat) => (
//               <div key={stat.label} className="flex flex-col gap-1">
//                 <p className="text-3xl font-medium leading-[1.3] md:text-[36px]" style={{ color: BRAND_RED_LIGHT }}>
//                   {stat.value}
//                 </p>
//                 <p className="text-[12px] font-medium uppercase tracking-[0.48px]" style={{ color: '#e6e8eb' }}>
//                   {stat.label}
//                 </p>
//               </div>
//             ))}
//           </div>
//           <Link
//             href="/pricing/compare"
//             className="inline-flex h-12 shrink-0 items-center justify-center gap-1.5 self-start rounded-full px-5 text-sm font-medium tracking-[0.1px] text-white transition-opacity hover:opacity-80 md:self-auto"
//             style={{ backgroundColor: BRAND_RED }}
//           >
//             Open the full comparison
//             <ArrowRightIcon className="size-5" strokeWidth={2} />
//           </Link>
//         </div>
//       </div>
//     </section>
//   );
// }

type PlanPopinProps = {
  plan: Plan;
  billing: BillingFrequency;
  onClose: () => void;
};

function PlanPopin({ plan, billing, onClose }: PlanPopinProps) {
  const { price } = plan.prices[billing];
  const isPriceText = !/^\d/.test(price);

  // Close on Escape, and prevent the page from scrolling underneath the modal
  // while it's open. We reserve the scrollbar gutter on <html> so that when
  // overflow:hidden hides the scrollbar, the space stays — preventing the
  // page (and any position:fixed nav/footer) from jumping rightward.
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);

    const root = document.documentElement;
    const previousScrollbarGutter = root.style.scrollbarGutter;
    const previousOverflow = document.body.style.overflow;
    root.style.scrollbarGutter = 'stable';
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      root.style.scrollbarGutter = previousScrollbarGutter;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-popin-heading"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="relative flex w-full max-w-xl flex-col gap-6 rounded-2xl bg-white p-6 shadow-xl sm:p-8"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 inline-flex size-8 items-center justify-center rounded-full text-foreground transition-colors hover:bg-black/5"
        >
          <XMarkIcon className="size-5" strokeWidth={1.75} />
        </button>

        <div className="flex flex-col gap-2 pr-8">
          <p className="text-[12px] font-medium uppercase tracking-[0.48px]" style={{ color: BRAND_RED }}>
            {plan.category}
          </p>
          <h3 id="plan-popin-heading" className="text-3xl font-medium leading-[1.2] text-foreground">
            {plan.name}
          </h3>
          <p className="font-sans text-sm leading-[1.5]" style={{ color: '#51535b' }}>
            {plan.description}
          </p>
        </div>

        <div className="flex items-end gap-1.5">
          {isPriceText ? (
            <p className="text-4xl font-medium leading-[1.2] text-foreground">{price}</p>
          ) : (
            <>
              <span className="pb-2 text-2xl font-medium leading-none text-foreground">$</span>
              <span className="text-5xl font-medium leading-[1] tracking-[-1px] text-foreground">{price}</span>
              <span className="pb-2 text-sm" style={{ color: '#51535b' }}>
                /month
              </span>
            </>
          )}
        </div>

        <a
          href={plan.ctaHref}
          onClick={() => {
            rememberSelectedTrialPlan(plan.trialPlanCode);
            onClose();
          }}
          className="inline-flex h-12 w-full items-center justify-center rounded-full bg-black px-5 text-sm font-medium tracking-[0.1px] text-white transition-opacity hover:opacity-80"
        >
          {plan.popinCtaLabel}
        </a>

        <div className="h-px w-full bg-black/10" />

        {plan.featuresIntro && (
          <p className="text-[13px] font-medium leading-[1.5]" style={{ color: BRAND_RED }}>
            {plan.featuresIntro}
          </p>
        )}

        <ul className="flex flex-col gap-3">
          {plan.features.map((feature) => (
            <li key={feature.label} className="flex items-start gap-2.5">
              <CheckIcon className="mt-1 size-4 shrink-0" strokeWidth={2.5} style={{ color: BRAND_RED }} />
              <span className="text-sm leading-[1.5] text-foreground">
                <span className="font-medium">{feature.label}:</span> {feature.value}
              </span>
            </li>
          ))}
        </ul>

        <p className="text-[12px] leading-[1.5]" style={{ color: '#51535b' }}>
          {plan.limitNote}
        </p>
      </div>
    </div>
  );
}
